import apiClient from "./ApiClient";
import {
  GuestFormDataWithCompletion,
} from "../models/StaticDataModel";
import {
  hasAllMandatoryData,
  isEmptyGuest,
  normalizeGenderIn,
  normalizeGuestTitleToLovCode,
} from "../utils/guestUtils";

/**
 * Adult/child intent of the slot the user clicked when creating a guest. Sent on POST /guests
 * and stored on the check-in submission so the public UI can bucket guests by kind on refresh.
 */
export type GuestKind = "ADULT" | "CHILD";

/** Per-field / per-SOAP-step outcome from PUT /guests/{id} (Opera business errors, etc.) */
export interface GuestUpdateFieldError {
  field: string;
  soapOperation: string;
  message: string;
  operaErrorCode?: string | null;
}

export interface GuestUpdateSummary {
  succeededSteps: string[];
  fieldErrors: GuestUpdateFieldError[];
  hasErrors: boolean;
}

export interface SaveGuestResult {
  profileId: number;
  summary: GuestUpdateSummary;
}

const IDENTIFICATION_CODE_SET = new Set([
  "002", "003", "004", "005", "006", "007", "008", "009",
  "011", "012", "013", "022", "023", "024", "025", "026",
  "027", "028", "029", "030", "032", "033", "034", "035",
  "036", "037", "038", "039", "051", "052", "103", "999",
]);

function normalizeIdentificationTypeToCode(
  raw: unknown,
  nationality?: string,
  placeOfBirthCode?: string
): string {
  const value = String(raw ?? "").trim();
  if (!value) return "";

  const up = value.toUpperCase();
  if (IDENTIFICATION_CODE_SET.has(up)) return up;

  const isDomestic = [nationality, placeOfBirthCode]
    .map((v) => String(v ?? "").trim().toUpperCase())
    .some((v) => v === "HR" || v === "HRV");

  const normalized = value.toLowerCase().replace(/[^a-z]/g, "");
  switch (normalized) {
    case "passport":
    case "pasport":
      return isDomestic ? "028" : "002";
    case "id":
    case "idcard":
    case "identitycard":
    case "personalid":
      return isDomestic ? "051" : "027";
    case "driverlicense":
    case "drivinglicense":
    case "driverlicence":
    case "drivinglicence":
      return isDomestic ? "103" : "032";
    default:
      return "";
  }
}

/** Maps GET /guests/{id} JSON (or stored CREATE preview payload) into {@link GuestFormDataWithCompletion}. */
export const mapRemoteGuestProfilePayloadToForm = (
  data: any,
  numericId: number
): GuestFormDataWithCompletion => {
  const gender = normalizeGenderIn(data.gender);

  let dobDay: number | null = null;
  let dobMonth: number | null = null;
  let dobYear: number | null = null;

  if (data.dateOfBirth) {
    if (
      typeof data.dateOfBirth.day !== "undefined" &&
      typeof data.dateOfBirth.month !== "undefined" &&
      typeof data.dateOfBirth.year !== "undefined"
    ) {
      dobDay = Number(data.dateOfBirth.day) || null;
      dobMonth = Number(data.dateOfBirth.month) || null;
      dobYear = Number(data.dateOfBirth.year) || null;
    } else if (typeof data.dateOfBirth === "string") {
      const d = new Date(data.dateOfBirth);
      if (!isNaN(d.getTime())) {
        dobDay = d.getDate();
        dobMonth = d.getMonth() + 1;
        dobYear = d.getFullYear();
      }
    }
  }

  const placeOfBirthCode =
    data.birthCountry?.code ??
    data.placeOfBirth?.code ??
    data.placeOfBirth ??
    "";

  const address = data.address ?? data.addressInfo ?? {};
  const addressCountryCode = address.country?.code ?? address.country ?? "";

  // Preview / GuestData often has root-level `email` string and `phone` string while Opera uses nested objects.
  let telId = "";
  let telType = "";
  let phoneNumber = "";
  let phoneUseType = "";
  let telPrimary = false;
  if (data.telephoneInfo && typeof data.telephoneInfo === "object") {
    telId = data.telephoneInfo.id ?? "";
    telType = data.telephoneInfo.type ?? "";
    phoneNumber = data.telephoneInfo.phoneNumber ?? "";
    phoneUseType = data.telephoneInfo.phoneUseType ?? "";
    telPrimary = Boolean(data.telephoneInfo.primaryInd);
  } else if (data.telephone && typeof data.telephone === "object") {
    telId = data.telephone.id ?? "";
    telType = data.telephone.type ?? "";
    phoneNumber = data.telephone.phoneNumber ?? "";
    phoneUseType = data.telephone.phoneUseType ?? "";
    telPrimary = Boolean(data.telephone.primaryInd);
  }
  if (!phoneNumber && typeof data.phone === "string") {
    phoneNumber = data.phone;
  }

  let emailId = "";
  let emailType = "";
  let emailAddress = "";
  if (data.emailInfo && typeof data.emailInfo === "object") {
    emailId = data.emailInfo.id ?? "";
    emailType = data.emailInfo.type ?? "";
    emailAddress = data.emailInfo.email ?? "";
  } else if (typeof data.email === "string") {
    emailAddress = data.email;
  } else if (data.email && typeof data.email === "object") {
    emailId = data.email.id ?? "";
    emailType = data.email.type ?? "";
    emailAddress = data.email.email ?? "";
  }

  const idInfo = data.identificationInfo ?? {};
  const normalizedIdType = normalizeIdentificationTypeToCode(
    idInfo.identification?.idType ?? data.travelDocType,
    data.nationality,
    placeOfBirthCode
  );

  const base: GuestFormDataWithCompletion = {
    id: numericId,
    title: data.title ?? "",
    firstName: data.firstName ?? "",
    middleName: data.middleName ?? "",
    lastName: data.lastName ?? "",
    gender,
    dateOfBirth: { day: dobDay, month: dobMonth, year: dobYear },
    placeOfBirth: placeOfBirthCode,
    nationality: data.nationality ?? "",
    telephoneInfo: {
      id: telId,
      type: telType,
      phoneNumber,
      phoneUseType,
      primaryInd: telPrimary,
    },
    emailInfo: {
      id: emailId,
      type: emailType,
      email: emailAddress,
    },
    addressInfo: {
      id: address.id ?? "",
      type: address.type ?? "",
      addressLine: address.addressLine ?? "",
      cityName: address.cityName ?? "",
      country: addressCountryCode,
      postalCode: address.postalCode ?? "",
    },
    identificationInfo: {
      id: idInfo.id ?? "",
      type: idInfo.type ?? "",
      identification: {
        idType: normalizedIdType,
        idNumber:
          idInfo.identification?.idNumber ??
          data.passportNumber ??
          data.idNumber ??
          "",
      },
    },
    newsletter: Boolean(data.newsletter) ?? false,
  };

  const completed = hasAllMandatoryData(base);
  const updateUser = !isEmptyGuest(base);

  return { ...base, completed, updateUser };
};

export const fetchGuestById = async (id: string): Promise<GuestFormDataWithCompletion> => {
  const response = await apiClient.get(`/guests/${id}`);
  return mapRemoteGuestProfilePayloadToForm(response.data, Number(id));
};

// ---------------------------------------------------------------------------
// Shared payload builder
// ---------------------------------------------------------------------------

/**
 * Optional per-field LOV maps used for last-mile validation before {@code POST /guests}. The
 * address-country LOV is the one resolved via {@code getLovForField('addressCountry', ...)} on
 * the caller side, so it already reflects the chain's per-field config and the empty-LOV
 * fallback. Passing {@code undefined} skips the live-LOV check and falls back to the loose
 * length/regex guard, matching pre-V14 behaviour.
 */
export interface GuestPayloadLovs {
  addressCountryLov?: Record<string, string>;
}

function buildGuestPayload(
  guest: GuestFormDataWithCompletion,
  titles?: Record<string, string>,
  lovs?: GuestPayloadLovs,
) {
  const rawCountry = String(guest.addressInfo?.country ?? "").trim();
  const countryCode = rawCountry.toUpperCase();
  const allowedCountryCodes = lovs?.addressCountryLov
    ? new Set(Object.keys(lovs.addressCountryLov))
    : null;
  const hasCountry = countryCode.length > 0;
  const countryAllowed = !hasCountry
    ? true
    : allowedCountryCodes
      ? allowedCountryCodes.has(countryCode)
      : /^[A-Z]{2,3}$/.test(countryCode);
  if (!countryAllowed) {
    throw new Error(`Invalid address country code: ${rawCountry}`);
  }

  const rawIdType = String(guest.identificationInfo?.identification?.idType ?? "").trim();
  const idTypeCode = rawIdType.toUpperCase();
  const hasIdType = idTypeCode.length > 0;
  const idTypeAllowed = !hasIdType || IDENTIFICATION_CODE_SET.has(idTypeCode);
  if (!idTypeAllowed) {
    throw new Error(`Invalid identification type code: ${rawIdType}`);
  }

  const titleForApi =
    titles && Object.keys(titles).length > 0
      ? normalizeGuestTitleToLovCode(guest.title, titles)
      : guest.title?.trim() ?? "";
  return {
    title: titleForApi,
    firstName: guest.firstName,
    middleName: guest.middleName,
    lastName: guest.lastName,
    gender: !guest.gender || guest.gender === "UNKNOWN" ? "male" : guest.gender,
    dateOfBirth: guest.dateOfBirth,
    birthCountry: { code: guest.placeOfBirth },
    nationality: guest.nationality,
    telephoneInfo: {
      id: guest.telephoneInfo.id,
      type: guest.telephoneInfo.type,
      phoneNumber: guest.telephoneInfo.phoneNumber,
      phoneUseType: guest.telephoneInfo.phoneUseType,
    },
    email: guest.emailInfo.email,
    address: {
      id: guest.addressInfo.id,
      type: guest.addressInfo.type,
      addressLine: guest.addressInfo.addressLine,
      cityName: guest.addressInfo.cityName,
      country: { code: countryCode },
      postalCode: guest.addressInfo.postalCode,
    },
    identificationInfo: {
      id: guest.identificationInfo.id,
      type: guest.identificationInfo.type,
      identification: {
        idType: idTypeCode,
        idNumber: guest.identificationInfo.identification.idNumber,
      },
    },
    newsletter: guest.newsletter,
    completed: guest.completed,
  };
}

// ---------------------------------------------------------------------------
// Create guest -- POST /guests
// ---------------------------------------------------------------------------

export const createGuest = async (
  guest: GuestFormDataWithCompletion,
  hotelId: string,
  reservationId: string,
  confirmationNumber: string,
  mainGuestId?: string,
  titles?: Record<string, string>,
  lovs?: GuestPayloadLovs,
  guestKind?: GuestKind,
): Promise<number> => {
  const canReplaceSubmission =
    guest.checkInSubmissionId != null &&
    (guest.syncStatus === "PENDING" || guest.syncStatus === "FAILED");
  const payload = {
    hotelId,
    reservationId,
    confirmationNumber,
    mainGuestId: mainGuestId ?? undefined,
    ...(guestKind ? { guestKind } : {}),
    ...(canReplaceSubmission
      ? { checkInSubmissionId: guest.checkInSubmissionId }
      : {}),
    ...buildGuestPayload(guest, titles, lovs),
  };

  const response = await apiClient.post("/guests", payload);
  const body = response.data ?? {};

  /** Check-in API returns submission row id (async Opera); use negative for client list convention. */
  const submissionRowId = Number(body.id);
  if (Number.isFinite(submissionRowId) && submissionRowId > 0) {
    return -Math.abs(submissionRowId);
  }

  const href = body?.data?.links?.[0]?.href;
  const idFromHref = href?.split("/")?.pop();
  const idFromBody = body?.data?.id ?? body?.id;
  const idFromHeader = response.headers?.location
    ? String(response.headers.location).split("/").pop()
    : undefined;

  return Number(idFromHref ?? idFromBody ?? idFromHeader ?? guest.id);
};

// ---------------------------------------------------------------------------
// Update guest -- PUT /guests/{profileId}
// ---------------------------------------------------------------------------

export const updateGuest = async (
  profileId: string | number,
  guest: GuestFormDataWithCompletion,
  titles?: Record<string, string>,
  lovs?: GuestPayloadLovs,
  /** Booking confirmation (session); stored on check-in submission, not the Opera profile id */
  confirmationNumber?: string,
): Promise<SaveGuestResult> => {
  const payload = {
    ...buildGuestPayload(guest, titles, lovs),
    ...(confirmationNumber?.trim()
      ? { confirmationNumber: confirmationNumber.trim() }
      : {}),
  };

  const response = await apiClient.put(
    `/guests/${encodeURIComponent(String(profileId))}`,
    payload
  );

  const body = response.data ?? {};
  const summary: GuestUpdateSummary = {
    succeededSteps: Array.isArray(body.succeededSteps) ? body.succeededSteps : [],
    fieldErrors: Array.isArray(body.fieldErrors) ? body.fieldErrors : [],
    hasErrors: Boolean(body.hasErrors),
  };

  // The backend's CheckInSubmissionResponse exposes both `id` (submission row PK) and
  // `profileId` (the Opera profile id we PUT to). We MUST echo back the Opera profile id —
  // reading `body.id` here used to leak the row id (e.g. 231) into `guest.id`, causing the
  // next save to PUT /guests/231 and Opera to reject with OWSNAME-000001 "Invalid Name Id".
  const returnedProfileId =
    body.profileId ?? body.data?.profileId ?? profileId;
  return {
    profileId: Number(returnedProfileId),
    summary,
  };
};

// ---------------------------------------------------------------------------
// Convenience: picks create or update based on whether the guest already
// exists in Opera (updateUser flag from fetchGuestById).
// ---------------------------------------------------------------------------

export const saveGuest = async (
  guest: GuestFormDataWithCompletion,
  hotelId: string,
  reservationId: string,
  confirmationNumber: string,
  mainGuestId?: string,
  titles?: Record<string, string>,
  lovs?: GuestPayloadLovs,
  guestKind?: GuestKind,
): Promise<SaveGuestResult> => {
  if (guest.updateUser || guest.syncStatus === "SYNCED") {
    return updateGuest(guest.id, guest, titles, lovs, confirmationNumber);
  }
  const profileId = await createGuest(
    guest,
    hotelId,
    reservationId,
    confirmationNumber,
    mainGuestId,
    titles,
    lovs,
    guestKind,
  );
  const summary: GuestUpdateSummary = {
    succeededSteps: [],
    fieldErrors: [],
    hasErrors: false,
  };
  return { profileId, summary };
};
